import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Plus, Globe, Lock, Spinner } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

export default function SaveTemplateModal({ open, onOpenChange, designId, defaultTitle, isAdmin }) {
  const [cats, setCats] = useState([]);
  const [title, setTitle] = useState(defaultTitle || "");
  const [categoryId, setCategoryId] = useState("");
  const [subId, setSubId] = useState("");
  const [publish, setPublish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSub, setNewSub] = useState("");
  const [addingSub, setAddingSub] = useState(false);

  const loadCats = async () => {
    try { const r = await api.get("/categories"); setCats(r.data); } catch (e) {}
  };

  useEffect(() => {
    if (open) { setTitle(defaultTitle || ""); setPublish(false); setNewSub(""); setAddingSub(false); loadCats(); }
  }, [open, defaultTitle]);

  const tops = cats.filter((c) => !c.parent_id);
  const subs = cats.filter((c) => c.parent_id === categoryId);
  const catName = (id) => cats.find((c) => c.id === id)?.name || null;

  const createSub = async () => {
    if (!newSub.trim() || !categoryId) return;
    try {
      const r = await api.post("/categories", { name: newSub.trim(), parent_id: categoryId });
      setCats((p) => [...p, r.data]);
      setSubId(r.data.id);
      setNewSub(""); setAddingSub(false);
      toast.success("Subcategory added");
    } catch (e) { toast.error("Could not add subcategory"); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Give your template a title"); return; }
    setSaving(true);
    try {
      const res = await api.post("/templates", {
        design_id: designId,
        title: title.trim(),
        category: catName(categoryId),
        subcategory: catName(subId),
        visibility: publish && isAdmin ? "public" : "private",
      });
      toast.success(res.data.visibility === "public" ? "Published to the marketplace!" : "Saved to your templates");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save template");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-md" data-testid="save-template-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <FloppyDisk size={20} weight="fill" className="text-blue-400" /> Save as template
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Template name</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Neon Club Night" data-testid="template-title-input" className="mt-1.5 bg-black/40 border-white/15" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Category</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {tops.map((c) => (
                <button key={c.id} onClick={() => { setCategoryId(c.id); setSubId(""); }} data-testid={`tmpl-cat-${c.id}`}
                  className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${categoryId === c.id ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                  {c.name}
                </button>
              ))}
              {tops.length === 0 && <span className="text-xs text-zinc-500">No categories yet.</span>}
            </div>
          </div>

          {categoryId && (
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Subcategory <span className="text-zinc-600 normal-case">(optional)</span></label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                {subs.map((c) => (
                  <button key={c.id} onClick={() => setSubId(subId === c.id ? "" : c.id)} data-testid={`tmpl-sub-${c.id}`}
                    className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${subId === c.id ? "bg-fuchsia-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                    {c.name}
                  </button>
                ))}
                {isAdmin && !addingSub && (
                  <button onClick={() => setAddingSub(true)} data-testid="add-sub-btn" className="px-2.5 h-8 rounded-lg text-xs bg-white/5 hover:bg-white/10 text-zinc-300 inline-flex items-center gap-1">
                    <Plus size={13} /> New
                  </button>
                )}
                {isAdmin && addingSub && (
                  <div className="flex items-center gap-1">
                    <Input autoFocus value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createSub(); }} placeholder="Subcategory" data-testid="new-sub-input" className="h-8 w-32 bg-black/40 border-white/15 text-xs" />
                    <Button size="sm" onClick={createSub} className="h-8 bg-fuchsia-600 hover:bg-fuchsia-500">Add</Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isAdmin ? (
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
              <div className="flex items-center gap-2">
                {publish ? <Globe size={18} className="text-green-400" /> : <Lock size={18} className="text-zinc-400" />}
                <div>
                  <p className="text-sm font-medium">{publish ? "Publish to marketplace" : "Keep private"}</p>
                  <p className="text-[11px] text-zinc-500">{publish ? "Everyone can find & use it" : "Only you can use it"}</p>
                </div>
              </div>
              <Switch checked={publish} onCheckedChange={setPublish} data-testid="publish-switch" />
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-zinc-400">
              <Lock size={16} /> <span className="text-xs">Saved privately to your templates.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-template-confirm" className="bg-blue-600 hover:bg-blue-500">
            {saving ? <Spinner size={16} className="animate-spin mr-1.5" /> : <FloppyDisk size={16} className="mr-1.5" />}
            {publish && isAdmin ? "Publish" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
